<template>
  <div
    class="lesson-video-player"
    :class="{ vertical }"
    @mouseover="hoverOver"
    @mouseleave="hoverLeave"
  >
    <div
      v-if="isPanda"
      id="lesson-panda-player"
      class="embed-responsive embed-responsive-16by9"
      v-html="oEmbed"
    />

    <video
      v-else-if="isStream"
      ref="videoPlayer"
      controls
      crossorigin
      id="lesson-video-player"
      class="w-100"
      :data-poster="lesson.picture"
    />

    <div
      v-else
      ref="videoPlayer"
      class="embed-responsive embed-responsive-16by9"
      id="lesson-video-player"
    >
      <iframe
        class="embed-responsive-item"
        :src="linkMedia"
        allowfullscreen
      ></iframe>
    </div>

    <background-player-controls
      v-if="isBackground"
      @play-pause="playOrPause"
      @mute-sound="muteOrSound"
      :videoState="videoState"
      :soundState="soundState"
      :mini-player="miniPlayer"
    />
    <button
      v-if="miniPlayer && playBtnVisible"
      type="button"
      class="btn btn-mini-player p-0 m-0"
      @click="playOrPause"
    >
      <i :class="ICON_MAP[videoState]"></i>
    </button>
  </div>
</template>

<script>
import BackgroundPlayerControls from "@/components/preview/BackgroundPlayerControls.vue";
import { vimeoParser, youtubeParser } from "@/functions/helpers";
import axios from "axios";
import Hls from "hls.js";
import isMobile from "ismobilejs";
import get from "lodash/get";
import Plyr from "plyr";
import "plyr/dist/plyr.css";

export default {
  components: { BackgroundPlayerControls },
  props: {
    lesson: {
      type: Object,
      default: () => ({})
    },
    isBackground: {
      type: Boolean,
      default: false
    },
    miniPlayer: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      oEmbed: "",
      player: null,
      hls: null,
      vertical: false,
      playBtnVisible: true,
      videoState: "paused",
      soundState: "muted",
      playerOptions: {
        autoplay: false,
        invertTime: false,
        vimeo: {
          playsinline: false
        },
        i18n: {
          speed: "Velocidade",
          quality: "Qualidade",
          qualityLabel: {
            0: "Auto"
          }
        },
        speed: {
          selected: sessionStorage.getItem("bp-player-speed") || 1,
          options: [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 3]
        }
      }
    };
  },
  computed: {
    ICON_MAP() {
      return {
        paused: "icon-play1",
        ended: "icon-play1",
        playing: "icon-pause2"
      };
    },
    isStream() {
      return (this.lesson?.link_media || "").toLowerCase().includes(".m3u8");
    },
    isVimeo() {
      return (this.lesson?.link_media || "").includes("vimeo");
    },
    isYoutube() {
      return (this.lesson?.link_media || "").includes("youtu");
    },
    isPanda() {
      return (this.lesson?.link_media || "").includes("pandavideo");
    },
    linkMedia() {
      if (this.isVimeo) {
        return `https://player.vimeo.com/video/${vimeoParser(
          this.lesson.link_media
        )}`;
      }
      if (this.isYoutube) {
        return `https://www.youtube.com/embed/${youtubeParser(
          this.lesson.link_media
        )}`;
      }
      return this.lesson.link_media;
    }
  },
  mounted() {
    this.bootstrap();
  },
  watch: {
    "lesson.link_media": function() {
      this.bootstrap();
    }
  },

  destroyed() {
    this.teardown();
  },

  methods: {
    async bootstrap() {
      await this.$nextTick();
      this.teardown();

      if (!this.lesson?.link_media) return;

      if (this.isPanda) {
        axios
          .get(
            `https://api-v2.pandavideo.com/oembed?url=${this.lesson.link_media}`
          )
          .then(response => this.initPanda(response.data))
          .catch(() => {});
        return;
      }

      if (!this.isStream) {
        if (this.isVimeo || this.isYoutube) this.initPlayer();
        return;
      }

      this.initStream();
    },

    teardown() {
      if (this.interval) {
        clearInterval(this.interval);
        this.interval = null;
      }

      if (this.hls) {
        try {
          this.hls.destroy();
        } catch (e) {
          // noop
        }
        this.hls = null;
      }

      if (this.player && typeof this.player.destroy === "function") {
        try {
          this.player.destroy();
        } catch (e) {
          // noop
        }
      }
      this.player = null;

      const video = this.$refs.videoPlayer;
      if (video && video.tagName === "VIDEO") {
        try {
          video.pause();
        } catch (e) {
          // noop
        }
        video.removeAttribute("src");
        while (video.firstChild) video.removeChild(video.firstChild);
        try {
          video.load();
        } catch (e) {
          // noop
        }
      }
    },

    initStream() {
      const video = this.$refs.videoPlayer;
      if (!video) return;

      // Safari (e alguns iOS/macOS) suportam HLS nativamente
      if (
        video.canPlayType("application/vnd.apple.mpegURL") &&
        !isMobile(window.navigator).android.device
      ) {
        const source = document.createElement("source");
        source.src = this.lesson.link_media;
        source.type = "application/vnd.apple.mpegURL";
        video.appendChild(source);
        this.addSubtitleTrack(video);
        this.initPlayer();
        return;
      }

      if (!Hls.isSupported()) return;

      this.addSubtitleTrack(video);

      const config = {
        xhrSetup: function(xhr, url) {
          xhr.withCredentials = false;
        },
        autoStartLoad: false
      };

      const hls = new Hls(config);
      this.hls = hls;

      const updateQuality = newQuality => {
        if (newQuality === 0) {
          hls.currentLevel = -1;
        } else {
          hls.levels.forEach((level, levelIndex) => {
            if (level.height === newQuality) {
              hls.currentLevel = levelIndex;
            }
          });
        }
      };

      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(this.lesson.link_media);
      });

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        hls.startLoad();
        const availableQualities = hls.levels.map(l => l.height).reverse();
        this.playerOptions.quality = {
          default: 360,
          options: [0, ...availableQualities],
          forced: true,
          onChange: e => updateQuality(e)
        };
        this.initPlayer();
      });

      hls.attachMedia(video);
    },

    initPlayer() {
      const hasSubtitle = !!this.lesson.subtitle_url;
      let controls = [
        "play-large",
        "play",
        "progress",
        "current-time",
        "mute",
        "volume",
        ...(hasSubtitle ? ["captions"] : []),
        "settings",
        "pip",
        "airplay"
      ];
      if (!isMobile(window.navigator).any) {
        controls = ["rewind", "fast-forward", ...controls, "fullscreen"];
      }
      this.playerOptions.controls = controls;
      if (hasSubtitle) {
        this.playerOptions.captions = { active: true, language: "pt", update: true };
      }

      const video = this.$refs.videoPlayer;

      this.player = new Plyr(video, this.playerOptions);
      window.player = this.player;
      var firstPlay = true;
      this.player.on("play", () => {
        if (firstPlay) {
          setTimeout(() => {
            this.player.currentTime = get(
              this.lesson,
              "progress.stopped_at",
              0
            );
          }, 1);
          firstPlay = false;
        }
      });

      this.player.on("ratechange", e => {
        const speed = e.detail.plyr.config.speed.selected;
        sessionStorage.setItem("bp-player-speed", speed);
      });

      this.player.on("ready", () => {
        setTimeout(() => {
          const iframe = this.$el.querySelector(".plyr__video-wrapper");
          if (iframe && iframe.offsetHeight > iframe.offsetWidth) {
            this.vertical = true;
          }
        }, 2000);
      });

      const saveCurrentPosition = () => {
        this.saveDuration({
          finished: this.player.currentTime === this.duration,
          duration: this.player.currentTime
        });
      };

      this.interval = setInterval(() => {
        if (this.player.playing) {
          saveCurrentPosition();
        }
      }, 20 * 1000);

      this.player.on("pause", saveCurrentPosition);

      this.player.on("ended", event => {
        setTimeout(() => {
          this.saveDuration({ finished: true, duration: 0 });
        }, 200);
        setTimeout(() => {
          this.$emit("ended");
        }, 1000);
      });
    },

    initPanda(data) {
      const params = this.isBackground
        ? new URLSearchParams({
            autoplay: true,
            muted: true,
            saveProgress: false,
            disableForward: false,
            hideControlsOnStart: true,
            restartAfterEnd: true,
            controls: false,
            mutedIndicatorIcon: false,
            mutedIndicatorTextTop: "",
            mutedIndicatorTextBottom: "",
            controlsColor: "transparent",
            color: "transparent"
          }).toString()
        : "";
      this.oEmbed = `<iframe
            id="panda-${data.video_external_id}"
            src="https://player-${data.pullzone_name}.tv.pandavideo.com.br/embed/?v=${data.video_external_id}&${params}"
            style="border:none;top:0"
            allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture"
            allowfullscreen=true
            class="embed-responsive-item"
          ></iframe>`;

      setTimeout(() => {
        window.pandascripttag = window.pandascripttag || [];
        window.pandascripttag.push(() => {
          const player = new PandaPlayer(`panda-${data.video_external_id}`, {
            onReady: () => {
              player.setCurrentTime(get(this.lesson, "progress.stopped_at", 0));

              const saveCurrentPosition = () => {
                this.saveDuration({
                  finished: player.getCurrentTime() === data.duration,
                  duration: player.getCurrentTime()
                });
              };

              this.interval = setInterval(() => {
                if (!player.isPaused()) {
                  saveCurrentPosition();
                }
              }, 20 * 1000);

              this.playBtnVisible = false;

              player.onEvent(({ message }) => {
                if (message === "panda_pause") {
                  saveCurrentPosition();
                  this.playBtnVisible = true;
                }

                if (message === "panda_ended") {
                  this.playBtnVisible = true;
                  setTimeout(() => {
                    this.saveDuration({ finished: true, duration: 0 });
                  }, 200);
                  setTimeout(() => {
                    this.$emit("ended");
                  }, 1000);
                }
              });
            }
          });
          window.player = player;
          this.player = player;
        });
      }, 1);
    },

    addSubtitleTrack(video) {
      if (!this.lesson.subtitle_url) return;
      const track = document.createElement("track");
      track.kind = "subtitles";
      track.label = "Português";
      track.srclang = "pt";
      track.src = this.lesson.subtitle_url;
      track.default = true;
      video.appendChild(track);
    },

    saveDuration({ finished, duration }) {
      if (this.lesson.id) {
        this.$store.dispatch("modules/setProgress", {
          lesson_id: this.lesson.id,
          stopped_at: parseInt(duration),
          finished_at: finished
        });
      }
    },
    playOrPause() {
      if (this.player.isPaused()) {
        this.videoState = "playing";
        this.player.play();
        this.playBtnVisible = false;
      } else {
        this.videoState = "paused";
        this.player.pause();
      }
    },
    muteOrSound() {
      if (this.player.getVolume() == 0) {
        this.player.setVolume(1);
        this.soundState = "sound";
      } else {
        this.player.setVolume(0);
        this.soundState = "muted";
      }
    },
    hoverOver() {
      if (this.isBackground) this.playBtnVisible = true;
    },
    hoverLeave() {
      if (this.isBackground) this.playBtnVisible = false;
    }
  }
};
</script>

<style lang="scss" scoped>
.lesson-video-player {
  animation: fadeInAnimation ease 2s;
  animation-iteration-count: 1;
  animation-fill-mode: forwards;
  opacity: 0;
  animation-delay: 0.5s;

  .embed-responsive {
    border-radius: 10px;
  }

  @keyframes fadeInAnimation {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
  &.vertical {
    max-width: 300px;
    margin: auto;
  }
}
</style>
